import { useCallback, useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { Images, Loader2, PencilLine, Settings2, BarChart3 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { AdminHeader, TabBar } from "@/components/admin/AdminChrome";
import AlbumPanel from "@/components/admin/AlbumPanel";
import PhotoManager from "@/components/admin/PhotoManager";
import SiteContent from "@/components/admin/SiteContent";
import Settings from "@/components/admin/Settings";
import TrafficWidget from "@/components/admin/TrafficWidget";

const TABS = [
  { id: "albums", label: "Albums & Photos", icon: Images, testId: "tab-albums-photos" },
  { id: "content", label: "Website Content", icon: PencilLine, testId: "tab-site-content" },
  { id: "traffic", label: "Traffic", icon: BarChart3, testId: "tab-traffic" },
  { id: "settings", label: "Settings", icon: Settings2, testId: "tab-settings" },
];

function useAlbums(enabled) {
  const [albums, setAlbums] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/albums");
      setAlbums(data);
      setSelectedId((prev) => (prev && data.some((a) => a.id === prev) ? prev : data[0]?.id ?? null));
    } catch {
      /* toast handled by children where needed */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (enabled) refresh();
  }, [enabled, refresh]);

  return { albums, selectedId, setSelectedId, loading, refresh };
}

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("albums");
  const { albums, selectedId, setSelectedId, loading, refresh } = useAlbums(!!user);

  if (user === null) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-blush" data-testid="admin-loading">
        <Loader2 className="h-8 w-8 animate-spin text-gold" />
      </div>
    );
  }
  if (user === false) return <Navigate to="/admin/login" replace />;

  const selectedAlbum = albums.find((a) => a.id === selectedId) || null;

  const handleLogout = async () => {
    await logout();
    navigate("/admin/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-blush" data-testid="admin-dashboard">
      <AdminHeader email={user.email} onLogout={handleLogout} />
      <main className="mx-auto max-w-7xl px-4 py-8 sm:px-8">
        <TabBar tabs={TABS} active={tab} onChange={setTab} />
        {loading ? (
          <div className="flex justify-center py-24">
            <Loader2 className="h-8 w-8 animate-spin text-gold" />
          </div>
        ) : tab === "albums" ? (
          <div className="grid gap-6 lg:grid-cols-[300px,1fr]">
            <AlbumPanel albums={albums} selectedId={selectedId} onSelect={setSelectedId} refresh={refresh} />
            <PhotoManager album={selectedAlbum} refresh={refresh} />
          </div>
        ) : tab === "content" ? (
          <SiteContent />
        ) : tab === "traffic" ? (
          <TrafficWidget />
        ) : (
          <Settings />
        )}
      </main>
    </div>
  );
}
